import {
  AddressInput,
  Button,
  Checkbox,
  CountryInput,
  DatePicker,
  FieldWrapper,
  Input,
  InputErrorMessage,
  Label,
  LoadingIndicator,
  PhoneInput,
  RadioGroup,
  SingleFileUploader,
} from '@commenda/kakashi';
import { defaultAddressSchema } from '@commenda/schema/address';
import { IAddress } from '@commenda/types';
import { FileBasic } from '@commenda/types/src/file/types';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { TSetNotification } from '@commenda/types/src/random';
import { classNames, fetcher } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { LegacyDocumentType } from '@prisma/client';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { KeyedMutator } from 'swr';
import { DirectorFormType } from '../Director/DirectorFormType';
import FormHeader from '../FormElements/FormHeader';
import Required from '../FormElements/Required';
import { getIdentityProof } from '../in/utils';
import { DirectorFormFields, directorSchema } from './schema';

const DirectorForm: DirectorFormType<DirectorBase> = ({
  director,
  companyDataMutate,
  setOpen,
  isAddingDirector,
  setIsAddingDirector,
  companyId,
  corporationId,
  token,
  setNotification,
  onErrorResolve,
  selectedDirectorIds,
  setSelectedDirectorIds,
  modalConfig,
  workRequestData,
}: {
  director?: DirectorBase;
  companyDataMutate: KeyedMutator<unknown>;
  isAddingDirector: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAddingDirector: React.Dispatch<React.SetStateAction<boolean>>;
  companyId: number;
  corporationId?: number;
  token: string;
  setNotification: TSetNotification;
  onErrorResolve?: () => void;
  selectedDirectorIds?: number[];
  selectedDirectorIdsRef?: React.MutableRefObject<number[]>;
  setSelectedDirectorIds?: React.Dispatch<React.SetStateAction<number[]>>;
  modalConfig?: {
    heading?: string;
    subheading?: string;
    isDirector?: boolean;
    isAuthorizedSignatory?: boolean;
    postSave?: (director: {
      id: number;
      directorFirstName: string;
      directorLastName: string;
    }) => void;
  };
  workRequestData?: { id: string };
}) => {
  const passportProofFile = getIdentityProof(director, corporationId);
  const [passportProof, setPassportProof] = useState<FileBasic | null>(
    passportProofFile
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    clearErrors,
    setError,
  } = useForm<DirectorFormFields>({
    resolver: zodResolver(directorSchema),
    defaultValues: {
      dateOfBirth: director?.dateOfBirth || '',
      directorFirstName: director?.directorFirstName,
      directorLastName: director?.directorLastName,
      email: director?.email,
      number: director?.number,
      personalDetails: {
        nationality: director?.personalDetails?.nationality,
      },
      passportProof,
      operatingAddress: director?.operatingAddress || defaultAddressSchema,
      corporationHasDirector: [
        {
          corporationId,
          additionalDirectorInformation: {
            hasSignificantControl:
              director?.corporationHasDirector?.find(
                (corpWithDir) =>
                  corpWithDir.corporationId === corporationId &&
                  corpWithDir.directorId === director.id
              )?.additionalDirectorInformation?.hasSignificantControl || false,
          },
        },
      ],
      incorporationInfo: {
        hasUsedDifferentName: director?.corporationHasDirector?.find(
          (corpWithDir) =>
            corpWithDir.corporationId === corporationId &&
            corpWithDir.directorId === director.id
        )?.incorporationInfo?.hasUsedDifferentName,
        jobTitle:
          director?.corporationHasDirector?.find(
            (corpWithDir) =>
              corpWithDir.corporationId === corporationId &&
              corpWithDir.directorId === director.id
          )?.incorporationInfo?.jobTitle || '',
      },
    },
  });

  useEffect(() => {
    if (passportProof) {
      setValue('passportProof', passportProof);
    }
  }, [passportProof]);

  const onSubmit = async (formData: DirectorFormFields) => {
    setIsAddingDirector(true);

    const workRequestId = workRequestData?.id;

    const payload = {
      directorFirstName: formData.directorFirstName,
      directorLastName: formData.directorLastName,
      dateOfBirth: formData.dateOfBirth,
      number: formData.number,
      email: formData.email,
      personalDetails: {
        nationality: formData?.personalDetails?.nationality,
      },
      companyId,
      operatingAddress: formData.operatingAddress,
      filesToAdd: [
        {
          // Use existing file ID if available, otherwise use -1 for new uploads
          id: passportProof?.id ?? passportProofFile?.id ?? -1,
          fileName: passportProof?.fileName,
          fileUrl: passportProof?.fileUrl,
          documentType: LegacyDocumentType.Passport,
        },
      ],
      corporationHasDirector: [
        {
          corporationId,
          additionalDirectorInformation: {
            isDirector: modalConfig?.isDirector || false,
            isAuthorizedSignatory: modalConfig?.isAuthorizedSignatory || false,
            memberControlType: '',
            hasSignificantControl:
              // We dynamically set the hasSignificantControl boolean in defaultValues by finding director that belongs to the current corporation, company, and directorID.
              //  It needs to be an array structure because of the shape of the incoming data.
              // Since this is for editing a director, there will only ever be one.
              //  Hence corporationHasDirector.0.additionalDirectorInformation.hasSignificantControl is safe.
              formData?.corporationHasDirector?.[0]
                .additionalDirectorInformation.hasSignificantControl,
          },
          incorporationInfo: {
            hasUsedDifferentName:
              formData?.incorporationInfo?.hasUsedDifferentName || false,
            jobTitle: formData?.incorporationInfo?.jobTitle,
          },
        },
      ],
      ...(workRequestId != null ? { wrkReqId: workRequestId } : {}),
    };

    const res = await fetcher(
      director?.id ? `director/${director.id}` : 'director',
      {
        method: 'POST',
        body: payload,
      },
      token
    );

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, please try again later or contact team Commenda if you need help.',
      });

      await companyDataMutate();
      setIsAddingDirector(false);
      return;
    }

    if (res.status === 409) {
      const error = await res.json();
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: error.message,
      });
      setIsAddingDirector(false);
      return;
    }

    await companyDataMutate();

    const resData = await res.json();

    if (setSelectedDirectorIds)
      setSelectedDirectorIds([...(selectedDirectorIds || []), resData.id]);

    if (modalConfig?.postSave) {
      modalConfig.postSave({
        id: resData.id,
        directorFirstName: resData.directorFirstName,
        directorLastName: resData.directorLastName,
      });
    }

    if (onErrorResolve) onErrorResolve();

    setIsAddingDirector(false);

    reset();

    setOpen(false);
  };

  return (
    <form
      className="flex flex-col max-w-[574px]"
      onSubmit={handleSubmit(onSubmit, (err) => {
        console.error(err);
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: 'Please enter all the required details.',
        });
      })}
    >
      <FormHeader
        heading={modalConfig?.heading}
        subheading={modalConfig?.subheading}
        className="p-4"
      />
      <div className="grow px-4 overflow-y-auto max-h-[calc(80vh-164px)]">
        <div className="w-full h-full flex flex-col gap-4">
          <FieldWrapper>
            <Label className="text-sm gap-0.5 text-neutral-1">
              Legal Name (as on your passport)
              <Required />
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="First name"
                {...register('directorFirstName')}
                error={errors?.directorFirstName?.message}
                defaultValue={watch('directorFirstName')}
              />
              <Input
                placeholder="Last name"
                {...register('directorLastName')}
                error={errors?.directorLastName?.message}
                defaultValue={watch('directorLastName')}
              />
            </div>
          </FieldWrapper>
          <div className="grid grid-cols-2 gap-2">
            <FieldWrapper>
              <Label className="text-sm text-neutral-1">
                Phone number
                <Required />
              </Label>

              <PhoneInput
                id="phoneNumber"
                placeholder=""
                className="h-[42px]"
                {...register('number')}
                onChange={(e) => {
                  setValue('number', e);
                  clearErrors('number');
                }}
                value={watch('number')}
                error={errors?.number?.message}
              />
            </FieldWrapper>

            <FieldWrapper>
              <Label className="text-sm text-neutral-1">
                Email <Required />
              </Label>

              <Input
                type="email"
                placeholder="Email"
                {...register('email')}
                error={errors?.email?.message}
                value={watch('email')}
              />
            </FieldWrapper>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FieldWrapper>
              <Label className="text-sm text-neutral-1">
                Date of birth <Required />
              </Label>
              <DatePicker
                hideCalendar
                date={watch('dateOfBirth')}
                setDate={(dt) => {
                  clearErrors('dateOfBirth');
                  setValue('dateOfBirth', dt);
                }}
                _triggerClassName="min-w-fit w-full"
                hasError={!!errors?.dateOfBirth?.message}
                {...register('dateOfBirth')}
                disabled={{
                  after: new Date(),
                }}
              />
              {errors?.dateOfBirth?.message ? (
                <InputErrorMessage message={errors?.dateOfBirth?.message} />
              ) : null}
            </FieldWrapper>

            <FieldWrapper>
              <Label className="text-sm text-neutral-1">
                Nationality
                <Required />
              </Label>

              <CountryInput
                placeholder="Search country"
                type="single"
                id="nationality"
                onValueChange={(ctr) => {
                  setValue('personalDetails.nationality', ctr);
                }}
                {...register('personalDetails.nationality')}
                error={
                  errors?.personalDetails?.nationality?.message &&
                  !watch('personalDetails.nationality')
                    ? errors?.personalDetails?.nationality?.message
                    : undefined
                }
                value={watch('personalDetails.nationality')}
              />
            </FieldWrapper>
          </div>
          <FieldWrapper>
            <Label className="text-neutral-1">
              Current Address
              <Required />
            </Label>
            <AddressInput
              address={watch('operatingAddress') as IAddress}
              onChange={(ad) => {
                setValue('operatingAddress', ad);
                if (ad.country !== watch('operatingAddress.country')) {
                  clearErrors('operatingAddress.state');
                }
              }}
              lastRowClassName="grid-cols-2"
              error={{
                country:
                  errors?.operatingAddress?.country?.message &&
                  !watch('operatingAddress.country')
                    ? errors?.operatingAddress?.country?.message
                    : undefined,
                addressLine1:
                  !watch('operatingAddress.addressLine1') &&
                  errors?.operatingAddress?.addressLine1?.message
                    ? errors?.operatingAddress?.addressLine1?.message
                    : null,
                addressLine2:
                  !watch('operatingAddress.addressLine2') &&
                  errors?.operatingAddress?.addressLine2?.message
                    ? errors?.operatingAddress?.addressLine2?.message
                    : null,
                city:
                  !watch('operatingAddress.city') &&
                  errors?.operatingAddress?.city?.message
                    ? errors?.operatingAddress?.city?.message
                    : null,
                postalCode:
                  !watch('operatingAddress.postalCode') &&
                  errors?.operatingAddress?.postalCode?.message
                    ? errors?.operatingAddress?.postalCode?.message
                    : null,
                state:
                  !watch('operatingAddress.state') &&
                  errors?.operatingAddress?.state?.message
                    ? errors?.operatingAddress?.state?.message
                    : undefined,
              }}
            />
          </FieldWrapper>

          <FieldWrapper>
            <Label>
              Upload Passport
              <span className="text-violet-solid ml-1">*</span>
            </Label>
            <SingleFileUploader
              companyId={companyId}
              setFile={(file) => {
                setPassportProof(file);
                clearErrors('passportProof');
              }}
              file={passportProof}
              error={errors?.passportProof?.message}
              setError={(msg: string) =>
                setError('passportProof', { type: 'custom', message: msg })
              }
              token={token}
              accept={{
                'application/pdf': ['.pdf'],
                'image/*': ['.png', '.jpg', '.jpeg'],
              }}
              maxSize={10 * 1024 * 1024} // 10MB
              documentType={LegacyDocumentType.Passport}
              _className="w-full"
            />
            {errors?.passportProof ||
            errors?.passportProof?.fileName ||
            errors?.passportProof?.fileUrl ? (
              <InputErrorMessage message="Please upload your passport." />
            ) : null}
          </FieldWrapper>

          <FieldWrapper>
            <Label className="text-sm text-neutral-1">
              Has the director used a different name for business purposes in
              the last 20 years?
              <Required />
            </Label>
            <RadioGroup
              value={
                watch('incorporationInfo.hasUsedDifferentName') === null
                  ? ''
                  : watch('incorporationInfo.hasUsedDifferentName')?.toString()
              }
              onValueChange={(value) => {
                setValue(
                  'incorporationInfo.hasUsedDifferentName',
                  value === 'true'
                );
                clearErrors('incorporationInfo.hasUsedDifferentName');
              }}
              className="flex flex-row space-x-4"
            >
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="hasUsedDifferentName-yes"
                  value="true"
                  checked={
                    watch('incorporationInfo.hasUsedDifferentName') === true
                  }
                  onChange={() =>
                    setValue('incorporationInfo.hasUsedDifferentName', true)
                  }
                />
                <Label htmlFor="hasUsedDifferentName-yes">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="hasUsedDifferentName-no"
                  value="false"
                  checked={
                    watch('incorporationInfo.hasUsedDifferentName') === false
                  }
                  onChange={() =>
                    setValue('incorporationInfo.hasUsedDifferentName', false)
                  }
                />
                <Label htmlFor="hasUsedDifferentName-no">No</Label>
              </div>
            </RadioGroup>
            {errors?.incorporationInfo?.hasUsedDifferentName?.message && (
              <InputErrorMessage
                message={errors.incorporationInfo.hasUsedDifferentName.message}
              />
            )}
          </FieldWrapper>

          <FieldWrapper>
            <Label className="text-sm text-neutral-1">
              Job title
              <Required />
            </Label>
            <Input
              placeholder="Enter what the person works as. For example, carpenter"
              {...register('incorporationInfo.jobTitle')}
              error={errors?.incorporationInfo?.jobTitle?.message}
              value={watch('incorporationInfo.jobTitle')}
            />
          </FieldWrapper>

          <div className="flex items-center gap-1 pb-4">
            <Checkbox
              id="hasSignificantControl"
              {...register(
                'corporationHasDirector.0.additionalDirectorInformation.hasSignificantControl'
              )}
              defaultChecked={
                !!watch(
                  'corporationHasDirector.0.additionalDirectorInformation.hasSignificantControl'
                )
              }
              onCheckedChange={(val) => {
                if (val) {
                  setValue(
                    'corporationHasDirector.0.additionalDirectorInformation.hasSignificantControl',
                    true
                  );
                } else {
                  setValue(
                    'corporationHasDirector.0.additionalDirectorInformation.hasSignificantControl',
                    false
                  );
                }
              }}
            />
            <Label htmlFor="hasSignificantControl" className="text-neutral-1">
              This person is a person with significant control (PSC).
            </Label>
          </div>
        </div>
      </div>

      <div className="w-full flex justify-end gap-3 border-t border-border-primary p-4">
        <Button
          type="button"
          variant="secondary"
          onClick={() => setOpen(false)}
        >
          <span>Cancel</span>
        </Button>
        <Button type="submit" variant="primary" disabled={isAddingDirector}>
          {isAddingDirector ? <LoadingIndicator size={32} /> : null}
          <span
            className={classNames(
              'flex items-center',
              isAddingDirector ? 'ml-2' : ''
            )}
          >
            Save
          </span>
        </Button>
      </div>
    </form>
  );
};

export default DirectorForm;
